/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useMemo } from 'react';
import {
  Button,
  Tooltip,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { RepoGraphEntry } from './types';
import { SymbolKind, buildRepoGraphModel, symbolKind } from './repoGraphModel';
import { HowItWorks } from '../common/HowItWorks';
import { formatRelativeTime } from './relativeTime';

// Synthesized fleet view for the Global selection: the consolidated Level-2
// artifact does not exist yet, so this overview is derived from the cached
// per-repo graphs and upgrades automatically (the page renders FleetGraph
// instead the day /global starts returning one). Every number here traces to
// a specific artifact — sha on the card, derivation in the header copy.

const useStyles = makeStyles(theme => ({
  intro: {
    fontSize: 13,
    marginBottom: theme.spacing(2),
    maxWidth: 720,
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: theme.spacing(2),
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    background: theme.palette.background.paper,
    padding: theme.spacing(1.5, 2),
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  stats: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    margin: theme.spacing(0.5, 0, 1),
  },
  kindRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    fontSize: 11,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  hubs: {
    fontSize: 12,
    marginBottom: theme.spacing(1.5),
    '& > span': { color: theme.palette.text.secondary },
  },
  sha: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
}));

const kindColor = (kind: SymbolKind, theme: Theme): string => {
  switch (kind) {
    case 'class':
      return theme.palette.primary.main;
    case 'function':
      return theme.palette.success.main;
    case 'concept':
      return theme.palette.warning.main;
    default:
      return theme.palette.text.secondary;
  }
};

const RepoCard = (props: {
  name: string;
  entry: RepoGraphEntry;
  updatedAt?: string;
  onOpen: (name: string) => void;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { entry } = props;

  const derived = useMemo(() => {
    if (!entry.doc) {
      // Giants never ship a doc — the build-time summary carries the
      // same card facts.
      const sum = entry.summary;
      if (!sum) return null;
      return {
        kinds: new Map(Object.entries(sum.kinds) as [SymbolKind, number][]),
        hubs: sum.top_connected.map(t => ({ label: t.label, deg: t.degree })),
      };
    }
    const model = buildRepoGraphModel(entry.doc);
    const kinds = new Map<SymbolKind, number>();
    for (const n of entry.doc.nodes) {
      const k = symbolKind(n);
      kinds.set(k, (kinds.get(k) ?? 0) + 1);
    }
    // Dedupe by label: env-templated repos repeat one symbol per env dir
    // (four module.deployment nodes), which reads as a stutter here.
    const hubs: { label: string; deg: number }[] = [];
    for (const [id, deg] of [...model.degree.entries()].sort((a, b) => b[1] - a[1])) {
      const label = entry.doc!.nodes.find(n => n.id === id)?.label ?? id;
      if (hubs.some(h => h.label === label)) continue;
      hubs.push({ label, deg });
      if (hubs.length === 3) break;
    }
    return { kinds, hubs };
  }, [entry.doc, entry.summary]);

  return (
    <div className={classes.card}>
      <div className={classes.cardHead}>
        <strong>{props.name}</strong>
        {entry.stats?.sha && (
          <span className={classes.sha}>@{entry.stats.sha.slice(0, 7)}</span>
        )}
      </div>
      {entry.stats ? (
        <>
          <div className={classes.stats}>
            {entry.stats.symbols} nodes · {entry.stats.files} source files ·{' '}
            {entry.stats.edges} edges
            {props.updatedAt && formatRelativeTime(props.updatedAt) && (
              <span title={new Date(props.updatedAt).toLocaleString()}>
                {' '}
                · updated {formatRelativeTime(props.updatedAt)}
              </span>
            )}
          </div>
          {derived && (
            <>
              <div className={classes.kindRow}>
                {[...derived.kinds.entries()].map(([k, count]) => (
                  <span key={k} style={{ color: kindColor(k, theme) }}>
                    {k} · {count}
                  </span>
                ))}
              </div>
              <div className={classes.hubs}>
                <span>most connected: </span>
                {derived.hubs.map((h, i) => (
                  <React.Fragment key={h.label + i}>
                    {i > 0 && ', '}
                    {h.label} ({h.deg})
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
          <Button size="small" variant="outlined" onClick={() => props.onOpen(props.name)}>
            Open graph
          </Button>
        </>
      ) : (
        <div className={classes.stats} role="status">
          {entry.status === 'error'
            ? `couldn't cache: ${entry.error ?? 'unknown error'} — opening retries`
            : 'caching latest graph…'}
        </div>
      )}
    </div>
  );
};

export const FleetOverview = (props: {
  repos: string[];
  entries: Record<string, RepoGraphEntry>;
  storeStatus: string | null;
  updatedAt?: Record<string, string>;
  onOpen: (name: string) => void;
}) => {
  const classes = useStyles();
  return (
    <div>
      <HowItWorks
        variant="plain"
        collapseKey="knowledgeGraph.howItWorks"
        defaultOpen={false}
        lead={
          <>
            Each repo's knowledge graph is its symbol map — classes, functions
            and concepts, connected by imports, calls and references — rebuilt
            on every merge to the default branch and served here from the
            fleet store. The sha chip on every card names the exact commit the
            graph was built from; "updated" is when the store received it.
          </>
        }
        steps={[
          {
            title: 'Extract',
            body:
              'CI runs graphify over the merged trunk (code-only) on every ' +
              'default-branch merge and emits graph.json — the full symbol ' +
              'graph for that commit.',
          },
          {
            title: 'Derive',
            body:
              'The same job derives serving views next to it: summary.json ' +
              '(the card stats), a directory tree and ≤3000-unit shards for ' +
              'giant graphs, and graph.sqlite + kgquery.py for millisecond ' +
              'indexed queries.',
          },
          {
            title: 'Publish',
            body:
              'graph.json uploads to the S3 store gzipped under its commit ' +
              'sha; the latest pointer moves only after everything else has ' +
              'landed. A companion 30-day workflow artifact carries ' +
              'graph.json plus graph.sqlite and kgquery.py for CI consumers.',
          },
          {
            title: 'Serve',
            body:
              'This workbench reads through the pointer with a 60-second ' +
              'cache: small graphs render whole, giants open as a directory ' +
              'overview and drill down shard by shard.',
          },
          {
            title: 'Review',
            body:
              'PR reviews stage the artifact and query the graph to map a ' +
              "change's blast radius — the 🧭 line in a review's sticky " +
              'comment is this graph being consulted.',
          },
        ]}
      />
      <Typography className={classes.intro} component="p">
        The consolidated fleet graph hasn't been published yet — this overview
        is derived from the {props.repos.length} repo graph
        {props.repos.length === 1 ? '' : 's'} below and upgrades to the real
        fleet graph automatically when it ships.
        {props.storeStatus && (
          <Tooltip title={props.storeStatus}>
            <Typography variant="caption" component="span" color="textSecondary">
              {' '}
              (store status:{' '}
              {/ENOENT|NoSuchKey|-> 404|does not exist/i.test(props.storeStatus)
                ? 'no fleet artifact yet'
                : props.storeStatus}
              )
            </Typography>
          </Tooltip>
        )}
      </Typography>
      <div className={classes.cards}>
        {props.repos.map(name => (
          <RepoCard
            key={name}
            name={name}
            entry={props.entries[name] ?? { status: 'pending' }}
            updatedAt={props.updatedAt?.[name]}
            onOpen={props.onOpen}
          />
        ))}
      </div>
    </div>
  );
};
